import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { CopyButton } from "../components/CopyButton";
import { Highlight, Spinner } from "../components/atoms";
import { loadEnglishIndex, loadOriginalIndex, loadVideo } from "../data/api";
import { useCollections } from "../data/collections";
import { formatCitation, youtubeTimeUrl } from "../lib/citation";
import {
  hasDevanagari,
  matchedLine,
  parsePhrase,
  resolvePassage,
  runQuery,
  tokenize,
} from "../lib/search";
import { formatTimestamp } from "../lib/transcript";
import type { CollectedQuote } from "../lib/types";

interface ResolvedHit {
  readonly videoId: string;
  readonly title: string;
  readonly speaker: string;
  readonly url: string;
  readonly t: number;
  readonly en: string;
  readonly orig: string;
  readonly score: number;
}

type Status = "idle" | "searching" | "done" | "error";

interface SearchState {
  readonly status: Status;
  readonly hits: readonly ResolvedHit[];
}

/** One slot in score order: still loading, resolved, or dropped (no match). */
type Slot = ResolvedHit | "pending" | "dropped";

const EXAMPLES = ["peace", "meditation", "service", "unity", "आत्मा"];
const MAX_RESULTS = 40;
const CONCURRENCY = 8;

/**
 * Resolve one ranked hit to a displayable line, or `null` to drop it. This is
 * where the video's transcript is fetched (and cached), so callers run several
 * at once rather than awaiting them in series.
 */
async function resolveOne(
  hit: { videoId: string; t: number; score: number },
  query: string,
  phrase: string | null,
): Promise<ResolvedHit | null> {
  const video = await loadVideo(hit.videoId);
  if (video === null) {
    return null;
  }
  const passage = resolvePassage(video.segments, hit.t);
  const line = matchedLine(passage, query);
  if (line === null) {
    return null;
  }
  if (
    phrase !== null &&
    !line.en.toLowerCase().includes(phrase) &&
    !line.orig.toLowerCase().includes(phrase)
  ) {
    return null;
  }
  return {
    videoId: video.id,
    title: video.title,
    speaker: video.speaker,
    url: video.url,
    t: line.t,
    en: line.en,
    orig: line.orig,
    score: hit.score,
  };
}

export function SearchScreen() {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(() => searchParams.get("q") ?? "");
  const [showOriginal, setShowOriginal] = useState(true);
  const [state, setState] = useState<SearchState>({ status: "idle", hits: [] });
  const { add, has } = useCollections();
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the box on arrival without scrolling the page to it.
  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, []);

  const terms = useMemo(() => tokenize(parsePhrase(query) ?? query), [query]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setState({ status: "idle", hits: [] });
      return;
    }
    let active = true;
    setState({ status: "searching", hits: [] });
    const timer = window.setTimeout(() => {
      const run = async () => {
        try {
          const indexes = [...(await loadEnglishIndex())];
          if (hasDevanagari(trimmed)) {
            indexes.push(...(await loadOriginalIndex()));
          }
          if (!active) {
            return;
          }
          const ranked = runQuery(indexes, trimmed, 80).slice(0, MAX_RESULTS);
          if (ranked.length === 0) {
            setState({ status: "done", hits: [] });
            return;
          }

          // Resolve with bounded concurrency, revealing the contiguous prefix
          // of finished results so they stream in top-down, in score order.
          const phrase = parsePhrase(trimmed)?.toLowerCase() ?? null;
          const slots: Slot[] = ranked.map((): Slot => "pending");
          const emit = (status: Status) => {
            const out: ResolvedHit[] = [];
            for (const slot of slots) {
              if (slot === "pending") {
                break;
              }
              if (slot !== "dropped") {
                out.push(slot);
              }
            }
            setState({ status, hits: out });
          };
          let cursor = 0;
          const worker = async () => {
            while (active) {
              const index = cursor;
              cursor += 1;
              if (index >= ranked.length) {
                return;
              }
              const resolved = await resolveOne(ranked[index], trimmed, phrase);
              if (!active) {
                return;
              }
              slots[index] = resolved ?? "dropped";
              emit("searching");
            }
          };
          await Promise.all(
            Array.from({ length: Math.min(CONCURRENCY, ranked.length) }, () =>
              worker(),
            ),
          );
          if (active) {
            emit("done");
          }
        } catch {
          if (active) {
            setState({ status: "error", hits: [] });
          }
        }
      };
      void run();
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [query]);

  return (
    <div>
      <header className="mb-6">
        <p className="eyebrow">Search</p>
        <h1 className="font-display text-3xl text-warm-brown">
          Find a line across every transcript
        </h1>
      </header>

      <div className="mb-2">
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search the discourses, in English or Devanagari…"
          className="field-line font-serif text-xl"
        />
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-warm-gray">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={showOriginal}
            onChange={(event) => setShowOriginal(event.target.checked)}
          />
          Show the original text
        </label>
        <span className="text-warm-gray/80">
          Tip: put a phrase in “quotes” to match it exactly.
        </span>
      </div>

      {state.status === "idle" ? (
        <div className="mb-8 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-warm-gray">Try</span>
          {EXAMPLES.map((term) => (
            <button
              key={term}
              type="button"
              onClick={() => setQuery(term)}
              className="rounded-full border border-hairline px-3 py-1 text-warm-brown transition-colors hover:border-gold/50 hover:text-terra"
            >
              {term}
            </button>
          ))}
        </div>
      ) : null}

      {state.status === "searching" && state.hits.length === 0 ? (
        <div className="flex items-center gap-3 text-warm-gray">
          <Spinner label="Searching" />
          <span>Searching the transcripts…</span>
        </div>
      ) : null}
      {state.status === "error" ? (
        <p className="text-warm-gray">
          The search index could not be loaded. Run{" "}
          <code className="font-mono text-sm">pnpm build:index</code> first.
        </p>
      ) : null}
      {state.status === "done" && state.hits.length === 0 ? (
        <p className="text-warm-gray">No lines match that search.</p>
      ) : null}

      <ul className="space-y-4">
        {state.hits.map((hit) => {
          const collected = has(hit.videoId, hit.t);
          const quote: CollectedQuote = {
            videoId: hit.videoId,
            title: hit.title,
            speaker: hit.speaker,
            url: hit.url,
            t: hit.t,
            en: hit.en,
            orig: hit.orig,
          };
          return (
            <li key={`${hit.videoId}#${hit.t}`} className="plate p-4 sm:p-5">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-warm-gray">
                <Link
                  to={`/video/${hit.videoId}?t=${hit.t}`}
                  className="font-medium text-terra hover:underline"
                >
                  {hit.title}
                </Link>
                <a
                  href={youtubeTimeUrl(hit.url, hit.t)}
                  target="_blank"
                  rel="noreferrer"
                  className="tabular-nums hover:text-terra"
                >
                  {hit.speaker} · {formatTimestamp(hit.t)}
                </a>
              </div>
              <p className="leading-relaxed text-warm-brown">
                <Highlight text={hit.en} terms={terms} />
              </p>
              {showOriginal && hit.orig.trim().length > 0 ? (
                <p className="mt-1 font-devanagari text-warm-brown-light">
                  <Highlight text={hit.orig} terms={terms} />
                </p>
              ) : null}
              <div className="mt-3 flex items-center gap-3 text-sm">
                <CopyButton
                  text={formatCitation({
                    quote: hit.en,
                    speaker: hit.speaker,
                    title: hit.title,
                    url: hit.url,
                    t: hit.t,
                  })}
                  label="Copy citation"
                  copiedLabel="Citation copied"
                  className="rounded-full border border-hairline px-3 py-1 text-warm-brown hover:border-gold/50"
                />
                <button
                  type="button"
                  onClick={() => add(quote)}
                  disabled={collected}
                  className="rounded-full bg-terra px-3 py-1 text-warm-white hover:bg-gold disabled:opacity-50"
                >
                  {collected ? "Collected" : "Collect"}
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {state.status === "searching" && state.hits.length > 0 ? (
        <div className="mt-5 flex items-center gap-2 text-sm text-warm-gray">
          <Spinner label="Loading more results" />
          <span>Loading more results…</span>
        </div>
      ) : null}
    </div>
  );
}
